import { Col, Input, Row } from "reactstrap";
import React, { useEffect, useMemo, useState } from "react";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { isEmpty } from "lodash";
import AppModal from "../AppModal";
import { handleRichTextOutput } from "../HandleRichTextOutput";

// A readable heading for a template: users often leave Title blank, so fall
// back to the Subject and finally a placeholder rather than showing "Title:".
const templateHeading = (template) =>
  template?.FriendlyName ||
  template?.Title ||
  template?.Subject ||
  "Untitled template";

const EmailTemplateSelection = ({ modal, toggle, templates, selectTemplate }) => {
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    // reset the search each time the picker opens
    if (modal) setSearchQuery("");
  }, [modal]);

  const searchResults = useMemo(() => {
    if (isEmpty(templates)) return [];
    const q = (searchQuery || "").toLowerCase();
    if (!q) return templates;
    const fields = ["FriendlyName", "Title", "Subject", "FromName", "FromEmail"];
    return templates.filter((template) =>
      fields.some((field) =>
        template?.[field]?.toString()?.toLowerCase()?.includes(q)
      )
    );
  }, [templates, searchQuery]);

  return (
    <AppModal
      open={modal}
      onClose={toggle}
      title="Choose an email template"
      subtitle="Pick a template to pre-fill the subject and message."
      icon={<EmailOutlinedIcon />}
      headerAccent="indigo"
      size="lg"
      hideFooter
    >
      <div className="mb-3">
        <div className="search-box">
          <Input
            type="search"
            className="form-control"
            placeholder="Search templates by name, subject or sender…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <i className="ri-search-line search-icon"></i>
        </div>
      </div>

      {isEmpty(searchResults) ? (
        <div className="text-center text-muted py-5">
          <EmailOutlinedIcon style={{ fontSize: 40, opacity: 0.4 }} />
          <div className="mt-2">
            {isEmpty(templates)
              ? "No email templates have been set up yet."
              : "No templates match your search."}
          </div>
        </div>
      ) : (
        <Row className="g-3">
          {searchResults.map((template) => {
            const bodyPreview = handleRichTextOutput(template?.BodyHtml, {
              previewOnly: true,
              clampLines: 3,
            });
            return (
              <Col md={6} key={template?.Id}>
                <div className="border rounded-3 p-3 h-100 d-flex flex-column bg-body">
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <h6 className="mb-1 fw-semibold text-truncate" title={templateHeading(template)}>
                      {templateHeading(template)}
                    </h6>
                    <button
                      type="button"
                      className="btn btn-sm btn-success flex-shrink-0"
                      onClick={() => {
                        selectTemplate(template);
                        toggle();
                      }}
                    >
                      Use template
                    </button>
                  </div>

                  {template?.Subject && (
                    <div className="text-body small mb-1">
                      <span className="text-muted">Subject: </span>
                      {template.Subject}
                    </div>
                  )}

                  {(template?.FromName || template?.FromEmail) && (
                    <div className="text-muted small mb-2">
                      <i className="ri-mail-send-line me-1" />
                      {template?.FromName}
                      {template?.FromEmail ? ` <${template.FromEmail}>` : ""}
                    </div>
                  )}

                  <div className="border-top pt-2 mt-auto small text-muted">
                    {bodyPreview || <em>No message body</em>}
                  </div>
                </div>
              </Col>
            );
          })}
        </Row>
      )}
    </AppModal>
  );
};

export default EmailTemplateSelection;
